import { zodResolver } from '@hookform/resolvers/zod';
import type { CountryCode, Organization } from '@noconflict/contracts';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BillingCurrencyNotice, useCreateOrganization } from '../../../entities/org';
import { identityErrorMessage } from '../../../shared/api/identity-error';
import { Button } from '../../../shared/ui/button';
import { CountrySelect } from '../../../shared/ui/country-select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../shared/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../shared/ui/form';
import { Input as TextInput } from '../../../shared/ui/input';
import { type Input, schema, type Values } from './model';

/**
 * Self-serve org creation (Q13). A further org may sit in a different country from the first, so the same
 * currency notice as sign-up appears under the country (Q16). `onCreated` gets the new org to navigate to.
 */
export function CreateOrgDialog({
  open,
  onOpenChange,
  onCreated,
  defaultCountry,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (org: Organization) => void;
  defaultCountry?: CountryCode;
}) {
  const { t } = useTranslation('org');
  const create = useCreateOrganization();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Input, unknown, Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', ...(defaultCountry ? { country: defaultCountry } : {}) },
  });

  function close(next: boolean) {
    if (!next) {
      form.reset();
      setError(null);
    }
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('create.title')}</DialogTitle>
          <DialogDescription>{t('create.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="grid gap-4"
            onSubmit={form.handleSubmit(async (values) => {
              setError(null);
              try {
                const org = await create.mutateAsync(values);
                close(false);
                onCreated(org);
              } catch (e) {
                setError(identityErrorMessage(e));
              }
            })}
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('create.name')}</FormLabel>
                  <FormControl>
                    <TextInput autoComplete="organization" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('create.country')}</FormLabel>
                  <FormControl>
                    <CountrySelect value={field.value} onValueChange={field.onChange} />
                  </FormControl>
                  <FormDescription>
                    <BillingCurrencyNotice country={field.value} />
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => close(false)}>
                {t('create.cancel')}
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {t('create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
